<script lang="ts">
/** Shared max-width + horizontal padding recipe for the transcript, empty state and composer overlay. */
export const CONVERSATION_MEASURE_CLASS = 'max-w-chat px-6 @conversation-roomy:px-12'
</script>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, useSlots, watch } from 'vue'
import { ArrowDownIcon } from '@solar-icons/vue/linear'
import AppButton from '../../primitives/AppButton/AppButton.vue'
import { isNearBottom } from '../../../utils/near-bottom'
import { vScrollFade } from '@/components/primitives/v-scroll-fade'

const props = withDefaults(
  defineProps<{
    logLabel: string
    scrollToBottomLabel: string
    /** Show the `#empty` slot instead of the message list. */
    isEmpty?: boolean
    /** Identifies the active thread; changing it re-pins the viewport to the bottom. */
    threadKey?: string | null
  }>(),
  {
    isEmpty: false,
    threadKey: null,
  },
)

const slots = useSlots()

const CLEARANCE = 32

const scrollerRef = ref<HTMLDivElement | null>(null)
const contentRef = ref<HTMLDivElement | null>(null)
const overlayRef = ref<HTMLDivElement | null>(null)

const overlayHeight = ref(0)

const stickToBottom = ref(true)

function onScroll() {
  const el = scrollerRef.value
  if (!el) return
  stickToBottom.value = isNearBottom(el.scrollTop, el.scrollHeight, el.clientHeight, 40)
}

function scrollToBottom() {
  const el = scrollerRef.value
  if (!el) return
  el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
  stickToBottom.value = true
}

let resizeObserver: ResizeObserver | null = null

let overlayObserver: ResizeObserver | null = null

onMounted(() => {
  if (overlayRef.value) overlayHeight.value = overlayRef.value.offsetHeight

  resizeObserver = new ResizeObserver(() => {
    if (!stickToBottom.value) return
    const el = scrollerRef.value
    if (!el) return
    el.scrollTop = el.scrollHeight
  })
  if (contentRef.value) resizeObserver.observe(contentRef.value)

  overlayObserver = new ResizeObserver(() => {
    overlayHeight.value = overlayRef.value?.offsetHeight ?? 0
  })
  if (overlayRef.value) overlayObserver.observe(overlayRef.value)
})

watch(contentRef, (el, previousEl) => {
  if (previousEl) resizeObserver?.unobserve(previousEl)
  if (el) resizeObserver?.observe(el)
})

watch(overlayRef, (el, previousEl) => {
  if (previousEl) overlayObserver?.unobserve(previousEl)
  if (el) overlayObserver?.observe(el)
  else overlayHeight.value = 0
})

watch(overlayHeight, async () => {
  if (!stickToBottom.value) return
  await nextTick()
  const el = scrollerRef.value
  if (!el) return
  el.scrollTop = el.scrollHeight
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  resizeObserver = null
  overlayObserver?.disconnect()
  overlayObserver = null
})

watch(
  () => props.threadKey,
  () => {
    stickToBottom.value = true
    const el = scrollerRef.value
    if (!el) return
    el.scrollTop = el.scrollHeight
  },
)
</script>

<template>
  <div class="@container relative min-h-0 flex-1">
    <div
      ref="scrollerRef"
      v-scroll-fade="{ anchor: 'end', endInset: slots.composer ? overlayHeight : 0 }"
      class="absolute inset-0 overflow-y-auto"
      role="log"
      aria-live="polite"
      :aria-label="logLabel"
      @scroll="onScroll"
    >
      <div
        v-if="isEmpty"
        :class="['mx-auto flex h-full w-full min-w-0 flex-col', CONVERSATION_MEASURE_CLASS]"
        :style="slots.composer ? { paddingBottom: overlayHeight + 'px' } : undefined"
      >
        <slot name="empty" />
      </div>
      <div
        v-else
        ref="contentRef"
        :class="[
          'mx-auto flex w-full min-w-0 flex-col',
          CONVERSATION_MEASURE_CLASS,
          'gap-8 py-16 @conversation-roomy:gap-12',
        ]"
        :style="slots.composer ? { paddingBottom: overlayHeight + CLEARANCE + 'px' } : undefined"
      >
        <slot />
      </div>
    </div>

    <div
      v-if="!stickToBottom"
      :class="[
        'pointer-events-none absolute inset-x-0 flex justify-center',
        slots.composer ? 'pb-6' : 'bottom-16',
      ]"
      :style="slots.composer ? { bottom: overlayHeight + 'px' } : undefined"
    >
      <AppButton
        size="sm"
        icon-only
        variant="surface"
        radius="full"
        elevated
        :start-icon="ArrowDownIcon"
        class="pointer-events-auto"
        :aria-label="scrollToBottomLabel"
        @click="scrollToBottom"
      />
    </div>

    <div v-if="slots.composer" ref="overlayRef" class="absolute inset-x-0 bottom-0 pb-16 pt-8">
      <div :class="['mx-auto w-full min-w-0', CONVERSATION_MEASURE_CLASS]">
        <slot name="composer" />
      </div>
    </div>
  </div>
</template>
